import isEqual from 'lodash/isEqual'
import { action, computed, makeObservable, observable } from 'mobx'

import { Alert } from '@avantstay/backoffice-core'

import { handleStoreError } from '@new/__global/utils/handleStoreError'

import { ObservableMutation } from '@queries/observable-mutation'
import { ObservableQuery } from '@queries/observable-query'
import { queryClient } from '@queries/query-client'
import { partialMatchKey } from '@tanstack/react-query'

import { stayTimelineQueryKeys } from '../queries/queryKeys'
import { getTimelineConversationQueryOptions } from '../queries/timelineConversationQuery'
import {
  DEFAULT_TIMELINE_FILTERS,
  DESTINATION_CHANNEL_TONES,
  SPOKEPHONE_LABEL,
} from '../StayTimeline.consts'
import { MessageDestination, TimelineFilterValues } from '../StayTimeline.types'
import {
  getOtaReplyLabel,
  getTimelineEvents,
  splitTimeline,
  toStayDetail,
} from '../StayTimeline.utils'
import {
  addInternalNote,
  AddInternalNoteParams,
  deleteInternalNote,
  DeleteInternalNoteParams,
  getGuestInboxStayDetail,
  sendOtaMessage,
  sendStayMessage,
  updateInternalNote,
  UpdateInternalNoteParams,
} from './api/stayTimelineArriere'

export class StayTimelineStore {
  private readonly _getBookingId: () => string | undefined

  filters: TimelineFilterValues = DEFAULT_TIMELINE_FILTERS

  private _stayQuery = new ObservableQuery(() => {
    const bookingId = this.bookingId

    return {
      queryKey: stayTimelineQueryKeys.stayFacts(bookingId),
      enabled: !!bookingId,
      queryFn: () => getGuestInboxStayDetail({ bookingId: bookingId! }),
    }
  })

  private _conversationQuery = new ObservableQuery(() => {
    const bookingId = this.bookingId

    return {
      ...getTimelineConversationQueryOptions({ bookingId, filters: this.filters }),
      // Keep the timeline on screen while the filters change, never across stays
      placeholderData: (previousData, previousQuery) => {
        if (!previousQuery) return undefined

        const isSameQuery = partialMatchKey(
          previousQuery.queryKey,
          stayTimelineQueryKeys.bookingConversations(bookingId),
        )

        return isSameQuery ? previousData : undefined
      },
    }
  })

  private _sendMessageMutation = new ObservableMutation({
    mutationFn: ({
      destination,
      ...args
    }: {
      destination: MessageDestination
      bookingId: string
      message: string
    }) => {
      return destination === 'ota' ? sendOtaMessage(args) : sendStayMessage(args)
    },
  })

  private _addNoteMutation = new ObservableMutation({
    mutationFn: (params: AddInternalNoteParams) => addInternalNote(params),
  })
  private _updateNoteMutation = new ObservableMutation({
    mutationFn: (params: UpdateInternalNoteParams) => updateInternalNote(params),
  })
  private _deleteNoteMutation = new ObservableMutation({
    mutationFn: (params: DeleteInternalNoteParams) => deleteInternalNote(params),
  })

  constructor(getBookingId: () => string | undefined) {
    this._getBookingId = getBookingId

    makeObservable<StayTimelineStore, '_split' | '_isResolved'>(this, {
      filters: observable.ref,
      _split: computed,
      _isResolved: computed,
      bookingId: computed,
      facts: computed,
      thread: computed,
      detail: computed,
      conversation: computed,
      notes: computed,
      timelineEvents: computed,
      isLoading: computed,
      error: computed,
      isNotFound: computed,
      isStayMissing: computed,
      isSending: computed,
      isAddingNote: computed,
      isUpdatingFilter: computed,
      isFiltered: computed,
      setFilters: action.bound,
      clearFilters: action.bound,
      refresh: action.bound,
      sendMessage: action.bound,
      addNote: action.bound,
      updateNote: action.bound,
      deleteNote: action.bound,
    })
  }

  private get _split() {
    return splitTimeline(this.thread?.timeline ?? [])
  }

  private get _isResolved() {
    if (!this.bookingId || this.isLoading) return false

    return !this._stayQuery.isRefreshing && !this._conversationQuery.isRefreshing
  }

  get bookingId(): string | undefined {
    return this._getBookingId()
  }

  get facts() {
    return this._stayQuery.data ?? undefined
  }

  get thread() {
    return this._conversationQuery.data ?? undefined
  }

  get detail() {
    const { facts, thread } = this

    return facts && thread ? toStayDetail({ facts, thread }) : undefined
  }

  get conversation() {
    return this._split.conversation
  }

  get notes() {
    return this._split.notes
  }

  get timelineEvents() {
    return getTimelineEvents({
      events: this.thread?.timeline ?? [],
      notes: this.filters.notes,
      channels: this.filters.channels,
    })
  }

  get isLoading() {
    return this._stayQuery.isLoading || this._conversationQuery.isLoading
  }

  get error() {
    return this._stayQuery.error ?? this._conversationQuery.error
  }

  get isNotFound() {
    return this._isResolved && !this.error && !this.facts && !this.thread
  }
  get isStayMissing() {
    return this._isResolved && !this.error && !this.facts
  }

  get isSending() {
    return this._sendMessageMutation.isPending
  }

  get isAddingNote() {
    return this._addNoteMutation.isPending
  }

  get isUpdatingFilter() {
    return this._conversationQuery.isPlaceholderData
  }

  private _invalidateConversations() {
    return queryClient.invalidateQueries({
      queryKey: stayTimelineQueryKeys.bookingConversations(this.bookingId),
    })
  }

  get isFiltered() {
    return !isEqual(this.filters, DEFAULT_TIMELINE_FILTERS)
  }

  setFilters(changes: Partial<TimelineFilterValues>) {
    this.filters = { ...this.filters, ...changes }
  }

  clearFilters() {
    this.filters = DEFAULT_TIMELINE_FILTERS
  }

  refresh() {
    void this._stayQuery.invalidate()
    void this._invalidateConversations()
  }

  private _showSentChannel(destination: MessageDestination) {
    const { channels } = this.filters
    const tone = DESTINATION_CHANNEL_TONES[destination]

    if (channels.length && !channels.includes(tone)) {
      this.setFilters({ channels: [...channels, tone] })
    }
  }

  async sendMessage(message: string, destination: MessageDestination) {
    const { bookingId } = this

    if (!bookingId || !message || this.isSending) return false

    const channelLabel =
      destination === 'ota'
        ? getOtaReplyLabel(this.thread?.booking?.otaBookingInfo?.channel)
        : SPOKEPHONE_LABEL

    if (!channelLabel) {
      Alert.Error(
        "This booking's OTA does not support messaging. Nothing was sent — send it over Spokephone.",
      )
      return false
    }

    const failedMessage = `The guest was not messaged through ${channelLabel}. Nothing was sent — try again.`

    try {
      const wasSent = await this._sendMessageMutation.mutate({ destination, bookingId, message })

      if (!wasSent) {
        Alert.Error(failedMessage)
        return false
      }

      this._showSentChannel(destination)
      void this._invalidateConversations()
      return true
    } catch {
      Alert.Error(failedMessage)
      return false
    }
  }

  async addNote(body: string) {
    const trimmedBooking = body.trim()

    if (!this.bookingId || !trimmedBooking || this.isAddingNote) return false

    try {
      await this._addNoteMutation.mutate({ bookingId: this.bookingId, body: trimmedBooking })

      if (!this.filters.notes) this.setFilters({ notes: true })

      Alert.Success('Note added')
      void this._invalidateConversations()
      return true
    } catch (error) {
      handleStoreError({
        action: 'stayTimeline.addNote',
        error,
        context: { bookingId: this.bookingId },
        userMessage: "Couldn't add the note. Try again.",
      })
      return false
    }
  }

  async updateNote(noteId: string, body: string) {
    const text = body.trim()

    if (!text) return false

    try {
      await this._updateNoteMutation.mutate({ noteId, body: text })

      Alert.Success('Note updated')
      await this._invalidateConversations()
      return true
    } catch (error) {
      handleStoreError({
        action: 'stayTimeline.updateNote',
        error,
        context: { bookingId: this.bookingId, noteId },
        userMessage: "Couldn't save your changes. Try again.",
      })
      return false
    }
  }

  async deleteNote(noteId: string) {
    try {
      const wasDeleted = await this._deleteNoteMutation.mutate({ noteId })

      if (!wasDeleted) throw new Error()

      Alert.Success('Note deleted')
      await this._invalidateConversations()
      return true
    } catch (error) {
      handleStoreError({
        action: 'stayTimeline.deleteNote',
        error,
        context: { bookingId: this.bookingId, noteId },
        userMessage: "Couldn't delete the note. Try again.",
      })
      return false
    }
  }
}
